import React, { useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import type { All8PageContent } from "@bcc/core-renderers";
import { reorderList } from "../LayoutEditor/utils";
import type { ArrayFieldConfig, Path } from "../../Pages/pageComponentLibrary";
import { cloneDeep } from "./pageEditorUtils";
import PageEditorFieldControl from "./PageEditorFieldControl";

const DEFAULT_ITEM_SUMMARY_KEYS = ["title", "description", "label", "name", "text", "heading", "tag"];

function getItemSummary(item: any, preferredKeys: string[] = DEFAULT_ITEM_SUMMARY_KEYS): string {
	if (item == null) return "Item";
	if (typeof item === "string") return item.trim().slice(0, 60) + (item.trim().length > 60 ? "…" : "") || "Item";
	const obj = typeof item === "object" ? item : {};
	for (const k of preferredKeys) {
		const v = obj[k];
		if (typeof v === "string" && v.trim()) return v.trim().slice(0, 80) + (v.trim().length > 80 ? "…" : "");
	}
	for (const v of Object.values(obj)) {
		if (typeof v === "string" && v.trim()) return (v as string).trim().slice(0, 80) + ((v as string).trim().length > 80 ? "…" : "");
	}
	return "Item";
}

export type PageEditorArrayFieldProps = {
	editable: boolean;
	field: ArrayFieldConfig;
	componentIndex: number;
	parentPath: Path;
	previewComponents: All8PageContent;
	updateComponentValue: (index: number, path: Path, value: any) => void;
	value: any;
	allowImageVariables?: boolean;
};

const PageEditorArrayField: React.FC<PageEditorArrayFieldProps> = ({
	editable,
	field,
	componentIndex,
	parentPath: fullPath,
	previewComponents,
	updateComponentValue,
	value,
	allowImageVariables
}) => {
	const items = Array.isArray(value) ? value : [];
	const summaryKeys = field.itemSummaryKeys ?? DEFAULT_ITEM_SUMMARY_KEYS;
	const [expandedItemIndex, setExpandedItemIndex] = useState<number | null>(null);

	return (
		<div className="space-y-2">
			{/* Section header: always visible, no collapse */}
			<div className="flex items-center justify-between gap-2">
				<span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{field.label}</span>
				<span className="text-xs text-slate-500">({items.length})</span>
				{editable && (
					<button
						type="button"
						onClick={() => {
							updateComponentValue(componentIndex, fullPath, [...items, cloneDeep(field.defaultItem ?? "")]);
							setExpandedItemIndex(items.length);
						}}
						className="shrink-0 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
					>
						Add
					</button>
				)}
			</div>

			<div className="space-y-2">
				{items.length === 0 && <p className="py-2 text-xs text-slate-500">No entries yet. Click Add to create one.</p>}
					{items.map((item: any, itemIndex: number) => {
						const itemPath = [...fullPath, itemIndex];
						const isItemExpanded = expandedItemIndex === itemIndex;
						const summary =
							field.itemType === "text" ? (typeof item === "string" ? item : String(item ?? "")) : getItemSummary(item, summaryKeys);

						return (
							<div key={`${field.label}-${itemIndex}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
								{/* Item row: collapsible, collapsed by default, shows summary */}
								<div
									role="button"
									tabIndex={0}
									onClick={() => setExpandedItemIndex((i) => (i === itemIndex ? null : itemIndex))}
									onKeyDown={(e) =>
										(e.key === "Enter" || e.key === " ") && setExpandedItemIndex((i) => (i === itemIndex ? null : itemIndex))
									}
									className="flex min-w-0 items-center gap-2 px-3 py-2 text-left transition hover:bg-slate-50"
									aria-expanded={isItemExpanded}
								>
									{isItemExpanded ? (
										<FiChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
									) : (
										<FiChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
									)}
									<span className="min-w-0 flex-1 truncate text-sm text-slate-700">
										{summary || `${field.itemLabel ?? "Item"} ${itemIndex + 1}`}
									</span>
								</div>

								{/* Expanded item content */}
								{isItemExpanded && (
									<div className="space-y-3 border-t border-slate-100 bg-slate-50/50 p-3">
										{field.itemType === "text" ? (
											<input
												type="text"
												value={item ?? ""}
												onChange={(e) => {
													const next = [...items];
													next[itemIndex] = e.target.value;
													updateComponentValue(componentIndex, fullPath, next);
												}}
												className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
											/>
										) : (
											<div className="space-y-3">
												{(field.fields ?? []).map((child, childIndex) => (
													<div key={`${field.label}-${itemIndex}-${childIndex}`}>
														<PageEditorFieldControl
															editable={editable}
															field={child}
															componentIndex={componentIndex}
															parentPath={itemPath}
															previewComponents={previewComponents}
															updateComponentValue={updateComponentValue}
															allowImageVariables={allowImageVariables}
														/>
													</div>
												))}
											</div>
										)}
										{editable && (
											<div className="flex flex-wrap items-center gap-2 text-xs">
												<button
													type="button"
													onClick={() => {
														updateComponentValue(componentIndex, fullPath, items.filter((_, i) => i !== itemIndex));
														setExpandedItemIndex(null);
													}}
													className="rounded-full border border-rose-200 px-2.5 py-1 font-semibold text-rose-600 transition hover:border-rose-300 hover:bg-rose-50"
												>
													Remove
												</button>
												<button
													type="button"
													disabled={itemIndex === 0}
													onClick={() =>
														updateComponentValue(
															componentIndex,
															fullPath,
															reorderList(items, itemIndex, Math.max(0, itemIndex - 1))
														)
													}
													className="rounded-full border border-slate-200 px-2.5 py-1 font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
												>
													Move up
												</button>
												<button
													type="button"
													disabled={itemIndex === items.length - 1}
													onClick={() =>
														updateComponentValue(
															componentIndex,
															fullPath,
															reorderList(items, itemIndex, Math.min(items.length - 1, itemIndex + 1))
														)
													}
													className="rounded-full border border-slate-200 px-2.5 py-1 font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
												>
													Move down
												</button>
											</div>
										)}
									</div>
								)}
							</div>
						);
					})}
				</div>
		</div>
	);
};

export default PageEditorArrayField;
